import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { ParentNoteService } from 'us-web-services';
import { Button, ButtonType } from '../Button';
import { Modal } from '../Modal';

// Parent-side private note about a caregiver. Empty comment on save deletes.
const NOTE_MAX = 300;

export interface SitterNoteModalNote {
  id?: number;
  comment?: string;
  updated?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  sitterId: number;
  parentId: number;
  initialNote?: SitterNoteModalNote;
  onSaved?: (note?: SitterNoteModalNote) => void;
}

export const SitterNoteModal = ({
  isOpen,
  onClose,
  sitterId,
  parentId,
  initialNote,
  onSaved,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [comment, setComment] = useState(initialNote?.comment ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setComment(initialNote?.comment ?? '');
      setError('');
    }
  }, [isOpen, initialNote]);

  const save = async (): Promise<void> => {
    setSaving(true);
    setError('');
    try {
      const existingId = initialNote?.id;
      const now = new Date().toISOString();
      if (existingId && !comment.trim()) {
        await ParentNoteService.delete(existingId);
        onSaved?.(undefined);
      } else if (existingId) {
        const resp = await ParentNoteService.patch(existingId, {
          id: existingId,
          comment,
        });
        const saved = (resp.data?.data ?? { id: existingId, comment }) as SitterNoteModalNote;
        onSaved?.({ ...saved, updated: saved.updated ?? now });
      } else if (comment.trim()) {
        const resp = await ParentNoteService.create({
          sitterId,
          parentId,
          comment,
        });
        const saved = (resp.data?.data ?? { comment }) as SitterNoteModalNote;
        onSaved?.({ ...saved, updated: saved.updated ?? now });
      }
      onClose();
    } catch {
      setError(t('components.modals.sitterNote.saveError'));
      toast.error(t('components.modals.sitterNote.saveErrorToast'));
    } finally {
      setSaving(false);
    }
  };

  const remaining = NOTE_MAX - comment.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('components.modals.sitterNote.title')}
      className='w-full max-w-xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            buttonType={ButtonType.primary}
            disabled={saving}
            onClick={save}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className='text-right italic text-sm mb-1'>
        {t('components.modals.sitterNote.charactersRemaining', { count: remaining })}
      </div>
      <textarea
        aria-label={t('components.modals.sitterNote.noteAriaLabel')}
        className='form-control w-full border border-neutral-300 rounded p-2 mb-2'
        rows={6}
        maxLength={NOTE_MAX}
        value={comment}
        onChange={(e): void => setComment(e.target.value)}
      />
      <p className='text-sm'>
        <i className='fa-solid fa-lock mr-1' aria-hidden='true' />
        {t('components.modals.sitterNote.privacyHint')}
      </p>
      {error && <p className='text-red-600 text-sm mt-2'>{error}</p>}
    </Modal>
  );
};
